import type {TaskDetailsResponse} from 'src/types/task-and-reminder';

import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter/index';

import {
  fetchTasks,
  fetchTaskTypes,
  resetTaskDetails,
} from 'src/action-creators/task-and-reminder';
import {
  selectIsFetchingTasks,
  selectTaskList,
  selectTaskTypes,
} from 'src/selectors/task-and-reminder';
import {
  buildDashboardTaskListParams,
  getDashboardTaskDueDisplay,
  getTaskIconName,
  TAB_CONFIGS,
} from 'src/utils/task-and-reminder';
import * as reduxApi from 'src/utils/redux-api-v2';

import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  ButtonTab,
  ButtonTabs,
} from '@spaced-out/ui-design-system/lib/components/ButtonTabs';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  BodyMediumBold,
  BodySmall,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';

import TaskAndReminderForm from 'src/components/task-and-reminder/form/task-and-reminder-form';

import {AnalyticsService} from 'src/analytics';
import {TASK_AND_REMINDER} from 'src/analytics/constants_event';
import css from './dashboard-tasks-widget.css';


const DASHBOARD_TABS = TAB_CONFIGS.filter(
  (c) => c.tabId === 'overdue' || c.tabId === 'upcoming',
);

type DashboardTabId = 'overdue' | 'upcoming';

type Props = {
  className?: string;
};

const DashboardTasksWidget = ({className}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {t, makeKey} = useLabelTranslation();
  const taskList = useSelector(selectTaskList);
  const isFetchingTasks = useSelector(selectIsFetchingTasks);
  const taskTypes = useSelector(selectTaskTypes);

  const [activeTab, setActiveTab] = React.useState<DashboardTabId>('overdue');
  const [editTaskId, setEditTaskId] = React.useState<number | null>(null);
  const [tabTaskCount, setTabTaskCount] = React.useState<{
    overdue?: number;
    upcoming?: number;
  }>({});

  React.useEffect(() => {
    dispatch(fetchTaskTypes());
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(fetchTasks(buildDashboardTaskListParams(activeTab)));
  }, [activeTab, dispatch]);

  React.useEffect(() => {
    const fetchTabCount = async (tab: DashboardTabId) => {
      const payload = Object.fromEntries(
        Object.entries({
          ...buildDashboardTaskListParams(tab),
          limit: 1,
        }).filter(([, value]) => value !== null),
      );
      try {
        const response = (await dispatch(
          reduxApi.post('tasks/list', payload, {}),
        )) as {total_records?: number} | undefined;
        setTabTaskCount((previous) => ({
          ...previous,
          [tab]: response?.total_records ?? 0,
        }));
      } catch {}
    };
    fetchTabCount('overdue');
    fetchTabCount('upcoming');
  }, [editTaskId]);

  const taskTypeLabelMap = React.useMemo(
    () => new Map(taskTypes.map((tt) => [tt.key, tt.label])),
    [taskTypes],
  );

  const handleViewAll = () => {
    history.push({
      pathname: '/tasks',
    });
  };

  const handleCloseEditPanel = () => {
    setEditTaskId(null);
    dispatch(resetTaskDetails());
  };

  const handleRowActivate = (task: TaskDetailsResponse) => {
    if (task.id == null) {
      return;
    }
    AnalyticsService.track(TASK_AND_REMINDER.TASK_CARD_OPENED, {
      task_id: task.id,
      open_source: 'dashboard',
      task_type: task.task_type,
      is_overdue: task.time_due ? new Date(task.time_due) < new Date() : false,
      is_system_task: task.creator_type === 'system',
    });
    setEditTaskId(task.id);
  };

  const handleRowKeyDown = (
    event: React.KeyboardEvent,
    task: TaskDetailsResponse,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRowActivate(task);
    }
  };

  const emptyMeta =
    activeTab === 'overdue'
      ? {
          titleKey: 'No_Overdue_Tasks',
          titleFallback: 'No overdue tasks',
          descKey: 'You_dont_have_any_overdue_tasks',
          descFallback:
            'You don\'t have any overdue tasks. Great job staying on top of things!',
        }
      : {
          titleKey: 'No_Upcoming_Tasks',
          titleFallback: 'No upcoming tasks',
          descKey: 'You_dont_have_any_upcoming_tasks',
          descFallback:
            'You don\'t have any upcoming tasks. Create a new task to get started.',
        };

  return (
    <>
      <Card classNames={{wrapper: className}}>
        <CardHeader className={css.cardHeader}>
          <CardTitle className={css.cardTitle}>
            <Button type="secondary" iconLeftName="check" size="small" />
            <SubTitleSmall>{t('All_Tasks', 'All Tasks')}</SubTitleSmall>
          </CardTitle>
          <CardActions>
            <Button
              type="ghost"
              size="small"
              onClick={handleViewAll}
              children={t('View_all', 'View all')}
              testId="dashboard-tasks-view-all"
            />
          </CardActions>
        </CardHeader>
        <CardContent className={css.cardBody}>
          <ButtonTabs
            isFluid
            selectedButtonTabId={activeTab}
            onButtonTabSelect={(tabId) => {
              if (tabId === 'overdue' || tabId === 'upcoming') {
                setActiveTab(tabId);
              }
            }}
            testId="dashboard-tasks-tabs"
          >
            {DASHBOARD_TABS.map((tabConfig) => {
              const tabId = tabConfig.tabId as DashboardTabId;
              const count = tabTaskCount[tabId];
              const tabLabel =
                tabId === 'overdue'
                  ? `${t('Overdue', 'Overdue')} ${count ?? 0}`
                  : `${t('Upcoming', 'Upcoming')} ${count ?? 0}`;
              return (
                <ButtonTab
                  key={tabConfig.tabId}
                  id={tabConfig.tabId}
                  iconName={tabConfig.iconName}
                  testId={`dashboard-tasks-tab-${tabConfig.tabId}`}
                >
                  {tabLabel}
                </ButtonTab>
              );
            })}
          </ButtonTabs>
          {isFetchingTasks ? (
            <CircularLoader size="large" className={css.loader} />
          ) : taskList.length === 0 ? (
            <div className={css.emptyWrap}>
              <EmptyState
                title={t(makeKey(emptyMeta.titleKey), emptyMeta.titleFallback)}
                description={t(
                  makeKey(emptyMeta.descKey),
                  emptyMeta.descFallback,
                )}
                imageVariant="file"
              />
            </div>
          ) : (
            <div className={css.taskList}>
              {taskList.map((task) => {
                const dueDisplay = getDashboardTaskDueDisplay(
                  task.time_due,
                  activeTab,
                  t,
                );
                return (
                  <div
                    key={String(task.id ?? task.title)}
                    className={css.taskRow}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRowActivate(task)}
                    onKeyDown={(e) => handleRowKeyDown(e, task)}
                    data-testid={generateTestId({
                      base: 'dashboard-tasks-row',
                      slot: String(task.id),
                    })}
                  >
                    <Icon
                      color="primary"
                      name={getTaskIconName(task.task_type || '')}
                      size="small"
                    />
                    <div className={css.taskText}>
                      <BodyMediumBold>
                        {task.title || t('Untitled_Task', 'Untitled Task')}
                      </BodyMediumBold>
                      <div className={css.metaLine}>
                        <BodySmall color="secondary">
                          {(task.task_type &&
                            taskTypeLabelMap.get(task.task_type)) ||
                            t('Generic', 'Generic')}
                        </BodySmall>
                        <span className={css.metaSeparator} aria-hidden>
                          •
                        </span>
                        {dueDisplay.tooltipBody ? (
                          <Tooltip body={dueDisplay.tooltipBody}>
                            <BodySmall
                              color={
                                dueDisplay.isDanger ? 'danger' : 'secondary'
                              }
                            >
                              {dueDisplay.shortLabel}
                            </BodySmall>
                          </Tooltip>
                        ) : (
                          <BodySmall
                            color={dueDisplay.isDanger ? 'danger' : 'secondary'}
                          >
                            {dueDisplay.shortLabel}
                          </BodySmall>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {editTaskId ? (
        <TaskAndReminderForm
          mode="edit"
          isOpen
          taskId={editTaskId}
          tab={activeTab}
          onClose={handleCloseEditPanel}
        />
      ) : null}
    </>
  );
};

export default DashboardTasksWidget;
